import React, { useEffect, useRef, useState } from "react";
import "./projects.css";
import { useSelector } from "react-redux";
import { info } from "../../assets/projectsInfo/info.js";

const items = [...info, ...info, ...info];

function Projects() {
  const es_en = useSelector((state) => state.translation);
  const [selectedProject, setSelectedProject] = useState(null);
  const videoRef = useRef(null);
  const carouselRef = useRef(null);
  const animationFrameRef = useRef(null);
  const lastFrameTimeRef = useRef(0);
  const isDraggingRef = useRef(false);
  const isHoveredRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartScrollLeftRef = useRef(0);
  const didDragRef = useRef(false);

  useEffect(() => {
    const carousel = carouselRef.current;

    if (!carousel || selectedProject) {
      return undefined;
    }

    const setInitialPosition = () => {
      carousel.scrollLeft = carousel.scrollWidth / 3;
    };

    const normalizeScrollPosition = () => {
      const sectionWidth = carousel.scrollWidth / 3;

      if (carousel.scrollLeft <= sectionWidth * 0.5) {
        carousel.scrollLeft += sectionWidth;
      } else if (carousel.scrollLeft >= sectionWidth * 1.5) {
        carousel.scrollLeft -= sectionWidth;
      }
    };

    const animate = (time) => {
      if (!lastFrameTimeRef.current) {
        lastFrameTimeRef.current = time;
      }

      const delta = time - lastFrameTimeRef.current;
      lastFrameTimeRef.current = time;

      if (!isDraggingRef.current && !isHoveredRef.current) {
        carousel.scrollLeft += 0.55 * (delta / 16.67);
        normalizeScrollPosition();
      }

      animationFrameRef.current = window.requestAnimationFrame(animate);
    };

    setInitialPosition();
    animationFrameRef.current = window.requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }
      lastFrameTimeRef.current = 0;
    };
  }, [selectedProject]);

  const goDetails = (project) => {
    if (!project.video) return;
    setSelectedProject(project);
    setTimeout(
      () =>
        videoRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        }),
      50,
    );
  };

  const normalizeDragPosition = () => {
    const carousel = carouselRef.current;

    if (!carousel) {
      return;
    }

    const sectionWidth = carousel.scrollWidth / 3;

    if (carousel.scrollLeft <= sectionWidth * 0.5) {
      carousel.scrollLeft += sectionWidth;
    } else if (carousel.scrollLeft >= sectionWidth * 1.5) {
      carousel.scrollLeft -= sectionWidth;
    }
  };

  const handlePointerDown = (event) => {
    const carousel = carouselRef.current;

    if (!carousel) {
      return;
    }

    isDraggingRef.current = true;
    didDragRef.current = false;
    dragStartXRef.current = event.clientX;
    dragStartScrollLeftRef.current = carousel.scrollLeft;
    carousel.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    const carousel = carouselRef.current;

    if (!carousel || !isDraggingRef.current) {
      return;
    }

    const deltaX = event.clientX - dragStartXRef.current;

    if (Math.abs(deltaX) > 4) {
      didDragRef.current = true;
    }

    carousel.scrollLeft = dragStartScrollLeftRef.current - deltaX;
    normalizeDragPosition();
  };

  const handlePointerUp = (event) => {
    if (!isDraggingRef.current) {
      return;
    }

    isDraggingRef.current = false;
    carouselRef.current?.releasePointerCapture?.(event.pointerId);
  };

  const handlePointerLeave = () => {
    if (!isDraggingRef.current) {
      isHoveredRef.current = false;
      return;
    }

    isDraggingRef.current = false;
  };

  const handleClickCapture = (event) => {
    if (!didDragRef.current) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    didDragRef.current = false;
  };

  return (
    <section className="projects section reveal" id="projects">
      <h2 className="section__title">{es_en ? "Projects" : "Proyectos"}</h2>
      <span className="section__subtitle">
        {es_en ? "Completed" : "Completados"}
      </span>

      {selectedProject && (
        <div className="btn_c">
          <button onClick={() => setSelectedProject(null)}>
            {es_en ? "Close" : "Cerrar"}
            <i className="uil uil-arrow-down home__scroll-arrow"></i>
          </button>
        </div>
      )}

      <div className="video__container" ref={videoRef}>
        {selectedProject && (
          <video className="video__show" controls key={selectedProject.id}>
            <source src={selectedProject.video} type="video/mp4" />
          </video>
        )}
      </div>

      {!selectedProject && (
        <div
          className="projects__carousel-wrap"
          ref={carouselRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onPointerLeave={handlePointerLeave}
          onMouseEnter={() => {
            isHoveredRef.current = true;
          }}
          onMouseLeave={() => {
            isHoveredRef.current = false;
          }}
          onClickCapture={handleClickCapture}
        >
          <div className="projects__track">
            {items.map((project, i) => (
              <div key={`${project.id}-${i}`} className="projects__box">
                <h4 className="projects__name">{project.name}</h4>

                <img
                  className={`image${project.video ? "" : " image--no-video"}`}
                  src={project.img}
                  alt={project.name}
                  onClick={() => goDetails(project)}
                  title={
                    !project.video
                      ? es_en
                        ? "No video available"
                        : "Sin video disponible"
                      : ""
                  }
                />

                <p className="projects__desc">
                  {es_en ? project.desc_en : project.desc_es}
                </p>

                <div className="deployAndVideo">
                  {project.deploy ? (
                    <a
                      href={project.deploy}
                      target="_blank"
                      rel="noreferrer"
                      className="projects__link"
                    >
                      <i className="uil uil-external-link-alt"></i>
                      {es_en ? "View" : "Ver"}
                    </a>
                  ) : (
                    <span className="projects__link projects__link--disabled">
                      <i className="uil uil-external-link-alt"></i>
                      {es_en ? "View" : "Ver"}
                    </span>
                  )}
                  {project.repository ? (
                    <a
                      href={project.repository}
                      target="_blank"
                      rel="noreferrer"
                      className="projects__link"
                    >
                      <i className="uil uil-github"></i>
                      {es_en ? "Repository" : "Repositorio"}
                    </a>
                  ) : (
                    <span className="projects__link projects__link--disabled">
                      <i className="uil uil-github"></i>
                      {es_en ? "Repository" : "Repositorio"}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export default Projects;
